import { renderButton, renderIconButton } from 'BomdisoftComponents/buttons/render';
import { renderDialog, renderDialogActions, renderDialogContent, renderDialogTitle } from 'BomdisoftComponents/dialog/render';
import { attributes, html } from 'BomdisoftComponents/html/index';
import { CHECK_ROUNDED_GLYPH, CONTENT_COPY_GLYPH } from 'BomdisoftComponents/icons/glyphs';
import { renderIcon } from 'BomdisoftComponents/icons/render';
import { renderToggleButton, renderToggleGroup } from 'BomdisoftComponents/inputs/toggle-group/render';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';
import { enumOf, persisted } from '@bomdisoft/store/persisted';
import {
  connectAgentAddressText,
  connectAgentChatgptLinkText,
  connectAgentChatgptStepText,
  connectAgentClaudeLinkText,
  connectAgentClaudeStepText,
  connectAgentClientText,
  connectAgentCopiedText,
  connectAgentCopyText,
  connectAgentDoneText,
  connectAgentPasteChatgptText,
  connectAgentPasteClaudeText,
  connectAgentProductText,
  connectAgentSignInText,
  connectAgentSignInWorkspacesText,
  connectAgentText,
  type SupportedLanguage,
  type Translation,
} from '@bomdisoft/translations';

export type ConnectAgentProduct = {
  key: string;
  name: string;
  serverAddress: string;
};

export const AGENT_CLIENTS = ['claude', 'chatgpt'] as const;

export type AgentClient = (typeof AGENT_CLIENTS)[number];

export const agentClient = persisted<AgentClient>({
  codec: enumOf(AGENT_CLIENTS),
  default: 'claude',
  in: ['local'],
  key: '@bomdisoft-connect-agent-client',
});

type ClientGuide = {
  help: string;
  link: Translation;
  name: string;
  paste: Translation;
  step: Translation;
};

export const CLIENT_GUIDES: Record<AgentClient, ClientGuide> = {
  chatgpt: { help: 'https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt', link: connectAgentChatgptLinkText, name: 'ChatGPT', paste: connectAgentPasteChatgptText, step: connectAgentChatgptStepText },
  claude: { help: 'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp', link: connectAgentClaudeLinkText, name: 'Claude', paste: connectAgentPasteClaudeText, step: connectAgentClaudeStepText },
};

const SIGN_IN_BY_PRODUCT: Record<string, Translation> = { clepit: connectAgentSignInWorkspacesText };

export const toConnectAgentSignIn = (product: ConnectAgentProduct | undefined) => (product && SIGN_IN_BY_PRODUCT[product.key]) ?? connectAgentSignInText;

export const stepAround = (template: string): { after: string; before: string } => {
  const at = template.indexOf('{link}');
  return at === -1 ? { after: '', before: template } : { after: template.slice(at + '{link}'.length), before: template.slice(0, at) };
};

export type AddressCopyOptions = { isCopied?: boolean; language: SupportedLanguage; testId: string; tooltipId: string };

export const renderAddressCopy = ({ isCopied = false, language, testId, tooltipId }: AddressCopyOptions) => {
  const label = isCopied ? connectAgentCopiedText[language] : connectAgentCopyText[language];
  return renderTooltip({
    children: renderIconButton({ attributes: { 'data-copied': isCopied, 'data-testid': testId }, children: renderIcon(isCopied ? CHECK_ROUNDED_GLYPH : CONTENT_COPY_GLYPH, { size: 18 }), label, size: 'small' }),
    id: tooltipId,
    placement: 'top',
    title: label,
  });
};

export type ConnectAgentBodyOptions = {
  client: AgentClient;
  copyTooltipId: string;
  isCopied?: boolean;
  labelId: string;
  language: SupportedLanguage;
  productKey?: string | null;
  products: readonly ConnectAgentProduct[];
  testId: string;
};

export const renderConnectAgentBody = ({ client, copyTooltipId, isCopied, labelId, language, productKey = null, products, testId }: ConnectAgentBodyOptions) => {
  const product = products.find(entry => entry.key === productKey) ?? products[0];
  const guide = CLIENT_GUIDES[client];
  const { after, before } = stepAround(guide.step[language]);
  const address = product?.serverAddress ?? '';
  const productChoice =
    products.length > 1
      ? renderToggleGroup({
          attributes: { 'aria-label': connectAgentProductText[language], 'data-testid': `${testId}-product` },
          children: products.map(entry => renderToggleButton({ attributes: { 'data-testid': `${testId}-product-${entry.key}` }, children: entry.name, isPressed: entry.key === product?.key, size: 'small', value: entry.key })),
          className: 'bomdisoft-connect-agent-dialog-choice',
        })
      : '';
  const clientChoice = renderToggleGroup({
    attributes: { 'aria-label': connectAgentClientText[language], 'data-testid': `${testId}-client` },
    children: AGENT_CLIENTS.map(entry => renderToggleButton({ attributes: { 'data-testid': `${testId}-client-${entry}` }, children: CLIENT_GUIDES[entry].name, isPressed: entry === client, size: 'small', value: entry })),
    className: 'bomdisoft-connect-agent-dialog-choice',
  });
  const field = html`<div class="bomdisoft-connect-agent-dialog-field"><span${attributes({ class: 'bomdisoft-connect-agent-dialog-field-label', id: labelId })}>${connectAgentAddressText[language]}</span><span class="bomdisoft-connect-agent-dialog-url-row"><output${attributes({ 'aria-labelledby': labelId, class: 'bomdisoft-connect-agent-dialog-url', 'data-testid': `${testId}-address` })}>${address}</output>${renderAddressCopy({ isCopied, language, testId: `${testId}-copy`, tooltipId: copyTooltipId })}</span></div>`;
  const steps = html`<ol${attributes({ class: 'bomdisoft-connect-agent-dialog-steps', 'data-testid': `${testId}-steps` })}><li>${before}<a${attributes({ class: 'bomdisoft-connect-agent-dialog-step-link', 'data-testid': `${testId}-help`, href: guide.help, rel: 'noopener noreferrer', target: '_blank' })}>${guide.link[language]}</a>${after}</li><li>${guide.paste[language]}</li><li${attributes({ 'data-testid': `${testId}-sign-in` })}>${toConnectAgentSignIn(product)[language]}</li></ol>`;
  return html`<div class="bomdisoft-connect-agent-dialog-connect">${productChoice}${field}${clientChoice}${steps}</div>`;
};

export type ConnectAgentDialogOptions = ConnectAgentBodyOptions & { titleId: string };

export const renderConnectAgentDialog = (options: ConnectAgentDialogOptions) =>
  renderDialog({
    children: html`${renderDialogTitle({ children: connectAgentText[options.language], titleId: options.titleId })}${renderDialogContent({ children: renderConnectAgentBody(options) })}${renderDialogActions({ children: renderButton({ attributes: { 'data-testid': `${options.testId}-done` }, children: connectAgentDoneText[options.language], variant: 'text' }) })}`,
    testId: options.testId,
    titleId: options.titleId,
  });
